/**
 * GP-06 boosters bar: Undo, Shuffle (player level 3), Magnet, Extra slot (level 12).
 * Shows inventory counts, a gem "+" when empty, and greys out when core says it can't apply.
 */
import * as Phaser from 'phaser';
import { app } from '../game/context.js';
import type { Rect } from '../game/layout.js';
import { C, FONT } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { dur } from '../fx/motion.js';
import type { BoosterKey } from '../config/content.js';

const ICON: Record<BoosterKey, string> = {
  undo: 'ico_boost_undo',
  shuffle: 'ico_boost_shuffle',
  magnet: 'ico_boost_magnet',
  extraSlot: 'ico_boost_extraslot',
};

interface Btn {
  key: BoosterKey;
  root: Phaser.GameObjects.Container;
  badge: Phaser.GameObjects.Container;
  label: Phaser.GameObjects.Text;
  badgeBg: Phaser.GameObjects.Graphics;
  enabled: boolean;
  x: number;
  y: number;
  r: number;
}

export class BoosterBar {
  private btns: Btn[] = [];

  constructor(
    private scene: Phaser.Scene,
    area: Rect,
    keys: BoosterKey[],
    private onUse: (k: BoosterKey) => void,
  ) {
    const u = vp.u;
    const n = keys.length;
    const r = Math.min(area.h * 0.42, (area.w / Math.max(4, n)) * 0.36, 34 * u);
    const gap = Math.min(area.w / n, r * 3.2);
    const x0 = area.x + area.w / 2 - (gap * (n - 1)) / 2;
    const y = area.y + area.h / 2;
    keys.forEach((key, i) => {
      const x = x0 + gap * i;
      const root = scene.add.container(x, y).setDepth(3200);
      const g = scene.add.graphics();
      g.fillStyle(C.ink, 1);
      g.fillCircle(0, r * 0.12, r + 3 * u);
      g.fillStyle(C.cream, 1);
      g.fillCircle(0, 0, r);
      g.lineStyle(3 * u, C.ink, 1);
      g.strokeCircle(0, 0, r);
      const f = app().art.get(ICON[key]);
      const icon = scene.add.image(0, 0, f.texture, f.frame);
      icon.setScale((r * 1.25) / Math.max(icon.width, icon.height));
      const badge = scene.add.container(r * 0.72, -r * 0.72);
      const badgeBg = scene.add.graphics();
      const label = scene.add
        .text(0, 0, '0', { fontFamily: FONT, fontSize: `${Math.round(r * 0.52)}px`, fontStyle: '700', color: '#ffffff' })
        .setOrigin(0.5);
      badge.add([badgeBg, label]);
      root.add([g, icon, badge]);
      const zone = scene.add.zone(0, 0, r * 2.3, r * 2.3).setInteractive({ useHandCursor: true });
      root.add(zone);
      zone.on('pointerup', () => {
        this.bounce(root);
        this.onUse(key);
      });
      this.btns.push({ key, root, badge, label, badgeBg, enabled: true, x, y, r });
    });
  }

  private bounce(root: Phaser.GameObjects.Container): void {
    const d = dur(160);
    if (d) this.scene.tweens.add({ targets: root, scale: 0.88, duration: d / 2, yoyo: true, ease: 'Quad.easeOut' });
  }

  /** Update counts and enabled state (enabled = core can* and round playing). */
  sync(counts: Record<BoosterKey, number>, can: Record<BoosterKey, boolean>): void {
    const u = vp.u;
    for (const b of this.btns) {
      const n = counts[b.key];
      b.enabled = can[b.key];
      b.root.setAlpha(b.enabled ? 1 : 0.42);
      const txt = n > 0 ? String(n) : '+';
      if (b.label.text !== txt) b.label.setText(txt);
      const br = b.r * 0.36;
      b.badgeBg.clear();
      b.badgeBg.fillStyle(n > 0 ? C.tomato : C.gem, 1);
      b.badgeBg.lineStyle(2.5 * u, C.ink, 1);
      b.badgeBg.fillCircle(0, 0, br);
      b.badgeBg.strokeCircle(0, 0, br);
    }
  }

  /** Enabled state per button (test hooks): a disabled one is drawn greyed out. */
  enabledMap(): Partial<Record<BoosterKey, boolean>> {
    return Object.fromEntries(this.btns.map((b) => [b.key, b.enabled]));
  }

  isEnabled(k: BoosterKey): boolean {
    return this.btns.find((b) => b.key === k)?.enabled ?? false;
  }

  /** CSS-independent positions for tests and tutorial pointers. */
  positions(): { key: BoosterKey; x: number; y: number }[] {
    return this.btns.map((b) => ({ key: b.key, x: b.x, y: b.y }));
  }
}
